/**
 * redact.js — KVKK/GDPR-compliant PII redaction utilities.
 *
 * Strips sensitive biometric fields from objects before they reach
 * external logging pipelines (Sentry, PostHog, etc.).
 *
 * Usage:
 *   import { redactBiometrics } from '../lib/redact'
 *   Sentry.captureException(err, { extra: redactBiometrics(snapshotData) })
 */

/** Fields that must never appear in logs. */
const SENSITIVE_BIOMETRIC_FIELDS = [
  'hrv',
  'recovery',
  'stress',
  'bodyBattery',
  'sleep',
  'cycle',
  'activeInjuries',
  'vo2max',
  'restingHR',
  'hrvBaseline',
  'weight',
  'height',
  'sex',
  'age',
]

/**
 * Removes sensitive biometric fields from a shallow object copy.
 * Does NOT deep-clone; nested objects are replaced with '[REDACTED]'.
 *
 * @param {Record<string, unknown>} obj
 * @returns {Record<string, unknown>}
 */
export function redactBiometrics(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj
  const out = { ...obj }
  for (const field of SENSITIVE_BIOMETRIC_FIELDS) {
    if (field in out) {
      out[field] = '[REDACTED]'
    }
  }
  return out
}

/**
 * Strips all keys whose values are objects (nested PII containers)
 * and all known sensitive fields.
 *
 * @param {Record<string, unknown>} obj
 * @returns {Record<string, unknown>}
 */
export function redactDeep(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj
  const out = {}
  for (const [key, val] of Object.entries(obj)) {
    if (SENSITIVE_BIOMETRIC_FIELDS.includes(key)) {
      out[key] = '[REDACTED]'
    } else if (val !== null && typeof val === 'object') {
      out[key] = '[OBJECT_REDACTED]'
    } else {
      out[key] = val
    }
  }
  return out
}

/**
 * Wipes all Omnisio cross-app cached data from localStorage.
 * Call on logout to comply with KVKK right-to-erasure requirements.
 */
export function wipeLocalCache() {
  const OMNISIO_CACHE_KEYS = [
    'omnisio_xapp_snapshot',
    'omnisio_xapp_baseline',
    'omnisio_xapp_recommendation',
    'omnisio_xapp_idtoken',
  ]
  for (const key of OMNISIO_CACHE_KEYS) {
    try {
      localStorage.removeItem(key)
    } catch {
      // Silently ignore storage errors (private browsing etc.)
    }
  }
}
